import React, { useState, useEffect } from 'react';
import tokenManager from '../utils/tokenManager';
import { ArrowLeft, Plus, Edit, Trash2, GripVertical, FileText, Upload, ChevronDown, ChevronRight, Download, Eye, EyeOff, Copy } from 'lucide-react';
import toast from 'react-hot-toast';

const NewSetlistView = ({ onBack }) => {
  const [setlists, setSetlists] = useState([]);
  const [selectedSetlist, setSelectedSetlist] = useState(null);
  const [songs, setSongs] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [newSetlistName, setNewSetlistName] = useState('');
  const [newSetlistDescription, setNewSetlistDescription] = useState('');
  const [newSetlistDuration, setNewSetlistDuration] = useState('');
  const [newSetlistWatermark, setNewSetlistWatermark] = useState(null);
  const [editingSetlist, setEditingSetlist] = useState(null);
  const [showEditSetlistModal, setShowEditSetlistModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', total_duration: '', watermark_data: null });
  const [editWatermark, setEditWatermark] = useState(null);
  const [showPDFDropdown, setShowPDFDropdown] = useState(false);
  const [newSong, setNewSong] = useState({
    song_name: '',
    duration: '',
    notes: '',
    is_medley: false,
    is_section: false
  });
  const [recentWatermarks, setRecentWatermarks] = useState([]);
  const [editingSong, setEditingSong] = useState(null);
  const [showEditSongModal, setShowEditSongModal] = useState(false);
  const [editSongForm, setEditSongForm] = useState({
    song_name: '',
    duration: '',
    notes: '',
    is_medley: false,
    is_section: false
  });
  const [editingSubSong, setEditingSubSong] = useState(null);
  const [showEditSubSongModal, setShowEditSubSongModal] = useState(false);
  const [editSubSongForm, setEditSubSongForm] = useState({
    song_name: '',
    duration: '',
    notes: ''
  });
  const [loading, setLoading] = useState(true);
  const [draggedSong, setDraggedSong] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedMedleys, setExpandedMedleys] = useState(new Set());
  const [showAddSubSongModal, setShowAddSubSongModal] = useState(false);
  const [selectedMedley, setSelectedMedley] = useState(null);
  const [newSubSong, setNewSubSong] = useState({
    song_name: '',
    duration: '',
    notes: ''
  });

  useEffect(() => {
    loadSetlists();
    loadRecentWatermarks();
  }, []);

  const loadRecentWatermarks = async () => {
    try {
      const response = await fetch('/api/setlists/recent-watermarks', {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRecentWatermarks(data);
      }
    } catch (error) {
      console.error('Error loading recent watermarks:', error);
    }
  };

  useEffect(() => {
    if (selectedSetlist) {
      loadSongs();
    }
  }, [selectedSetlist]);

  // Close PDF dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPDFDropdown) {
        const dropdownContainer = event.target.closest('div[style*="position: relative"]');
        if (!dropdownContainer) {
          setShowPDFDropdown(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showPDFDropdown]);

  // Calculate total duration from songs
  const calculateTotalDuration = (songsList) => {
    let totalMinutes = 0;
    songsList.forEach(song => {
      if (song.duration) {
        const duration = song.duration.trim();
        const parts = duration.split(':');
        
        if (parts.length === 2) {
          // Format like "4:30" or "00:12"
          totalMinutes += parseInt(parts[0]) + (parseInt(parts[1]) / 60);
        } else if (parts.length === 1 && !isNaN(parseInt(duration))) {
          // Just a number like "12" - treat as minutes
          totalMinutes += parseInt(duration);
        }
      }
    });
    return `${Math.round(totalMinutes)}min`;
  };

  const loadSetlists = async () => {
    try {
      const response = await fetch('/api/setlists', {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Add song counts to setlists
        const setlistsWithCounts = await Promise.all(data.map(async (setlist) => {
          try {
            const songsResponse = await fetch(`/api/setlists/${setlist.id}/songs`, {
              headers: {
                'Authorization': `Bearer ${tokenManager.getToken()}`
              }
            });
            if (songsResponse.ok) {
              const songs = await songsResponse.json();
              return { ...setlist, songCount: songs.length, calculatedDuration: calculateTotalDuration(songs) };
            }
            return { ...setlist, songCount: 0, calculatedDuration: '0:00' };
          } catch {
            return { ...setlist, songCount: 0, calculatedDuration: '0:00' };
          }
        }));
        setSetlists(setlistsWithCounts);
      } else {
        toast.error('Failed to load setlists');
      }
    } catch (error) {
      toast.error('Failed to load setlists');
      console.error('Error loading setlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSongs = async () => {
    try {
      const response = await fetch(`/api/setlists/${selectedSetlist.id}/songs`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // For each medley, fetch its sub-songs
        const songsWithSubSongs = await Promise.all(data.map(async (song) => {
          if (song.is_medley) {
            try {
              const subSongsResponse = await fetch(`/api/songs/${song.id}/sub-songs`, {
                headers: {
                  'Authorization': `Bearer ${tokenManager.getToken()}`
                }
              });
              if (subSongsResponse.ok) {
                const subSongs = await subSongsResponse.json();
                return { ...song, sub_songs: subSongs };
              }
            } catch (error) {
              console.error('Error loading sub-songs for song:', song.id, error);
            }
          }
          return song;
        }));
        
        setSongs(songsWithSubSongs);
      } else {
        toast.error('Failed to load songs');
      }
    } catch (error) {
      toast.error('Failed to load songs');
      console.error('Error loading songs:', error);
    }
  };

  const createSetlist = async (e) => {
    e.preventDefault();
    if (!newSetlistName.trim()) {
      toast.error('Please enter a setlist name');
      return;
    }

    try {
      const response = await fetch('/api/setlists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({
          name: newSetlistName.trim(),
          description: newSetlistDescription.trim() || null,
          total_duration: newSetlistDuration.trim() || null,
          watermark_data: newSetlistWatermark
        })
      });

      if (response.ok) {
        toast.success('Setlist created successfully');
        setNewSetlistName('');
        setNewSetlistDescription('');
        setNewSetlistDuration('');
        setNewSetlistWatermark(null);
        setShowCreateModal(false);
        loadSetlists();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create setlist');
      }
    } catch (error) {
      toast.error('Failed to create setlist');
      console.error('Error creating setlist:', error);
    }
  };

  const addSong = async (e) => {
    e.preventDefault();
    if (!newSong.song_name.trim()) {
      toast.error('Please enter a song name');
      return;
    }

    try {
      const response = await fetch(`/api/setlists/${selectedSetlist.id}/songs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({
          song_name: newSong.song_name.trim(),
          duration: newSong.is_section ? null : (newSong.duration.trim() || null),
          notes: newSong.notes.trim() || null,
          is_medley: newSong.is_section ? false : newSong.is_medley,
          is_section: newSong.is_section,
          order_index: 1 // Will be auto-assigned by backend
        })
      });

      if (response.ok) {
        toast.success(newSong.is_section ? 'Section divider added successfully' : 'Song added successfully');
        setNewSong({ song_name: '', duration: '', notes: '', is_medley: false, is_section: false });
        setShowAddSongModal(false);
        loadSongs();
        loadSetlists(); // Refresh setlist counts
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add song');
      }
    } catch (error) {
      toast.error('Failed to add song');
      console.error('Error adding song:', error);
    }
  };

  const updateSong = async (songId, updates) => {
    try {
      const response = await fetch(`/api/songs/${songId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        toast.success('Song updated successfully');
        loadSongs();
        loadSetlists(); // Refresh setlist counts
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update song');
      }
    } catch (error) {
      toast.error('Failed to update song');
      console.error('Error updating song:', error);
    }
  };

  const deleteSong = async (songId, songName) => {
    if (!window.confirm(`Are you sure you want to delete "${songName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/songs/${songId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        toast.success('Song deleted successfully');
        loadSongs();
        loadSetlists(); // Refresh setlist counts
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete song');
      }
    } catch (error) {
      toast.error('Failed to delete song');
      console.error('Error deleting song:', error);
    }
  };

  const toggleBlind = async (song) => {
    try {
      const response = await fetch(`/api/songs/${song.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({
          ...song,
          is_blinded: !song.is_blinded
        })
      });

      if (response.ok) {
        const updatedSong = await response.json();
        setSongs(songs.map(s => s.id === song.id ? updatedSong : s));
        toast.success(`Song ${updatedSong.is_blinded ? 'blinded' : 'un-blinded'}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to toggle blind');
      }
    } catch (error) {
      console.error('Error toggling blind:', error);
      toast.error('Failed to toggle blind');
    }
  };

  // Three PDF generation options
  const generatePDFClean = () => {
    if (!selectedSetlist) {
      toast.error('Please select a setlist first');
      return;
    }
    // Always exclude blinded songs from the main Generate PDF button
    generateSetlistPDFExcludeBlinded(selectedSetlist.id, selectedSetlist.name, false, false);
    setShowPDFDropdown(false);
  };

  const generatePDFWithMedleys = () => {
    if (!selectedSetlist) {
      toast.error('Please select a setlist first');
      return;
    }
    generateSetlistPDF(selectedSetlist.id, selectedSetlist.name, false, true); // No notes, with medleys
    setShowPDFDropdown(false);
  };

  const generatePDFComplete = () => {
    if (!selectedSetlist) {
      toast.error('Please select a setlist first');
      return;
    }
    generateSetlistPDF(selectedSetlist.id, selectedSetlist.name, true, true); // With notes and medleys
    setShowPDFDropdown(false);
  };

  const startEditSetlist = (setlist) => {
    setEditingSetlist(setlist);
    setEditForm({ 
      name: setlist.name, 
      description: setlist.description || '',
      total_duration: setlist.total_duration || ''
    });
    // Load existing watermark if available
    // Don't set to null if watermark exists, leave undefined to preserve server data
    if (setlist.watermark_data) {
      setEditWatermark(setlist.watermark_data);
    } else {
      setEditWatermark(undefined);
    }
    setShowEditSetlistModal(true);
  };

  const cancelEditSetlist = () => {
    setEditingSetlist(null);
    setEditForm({ name: '', description: '', total_duration: '' });
    setEditWatermark(undefined);
    setShowEditSetlistModal(false);
  };

  const saveEditSetlist = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) {
      toast.error('Please enter a setlist name');
      return;
    }

    try {
      const updateData = { ...editForm };
      
      // Handle watermark data
      if (editWatermark === null) {
        // Explicitly remove watermark if it was cleared
        updateData.watermark_data = null;
      } else if (editWatermark) {
        // Add new or existing watermark data
        updateData.watermark_data = editWatermark;
      }
      // If editWatermark is undefined, don't include watermark_data in update
      // This preserves the existing watermark on the server
      
      console.log('Updating setlist with data:', {
        ...updateData,
        watermark_data: updateData.watermark_data ? 'Base64 data present' : 'No watermark'
      });

      const response = await fetch(`/api/setlists/${editingSetlist.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedSetlist = await response.json();
        toast.success('Setlist updated successfully');
        setEditingSetlist(null);
        setEditForm({ name: '', description: '', total_duration: '' });
        setEditWatermark(undefined);
        setShowEditSetlistModal(false);
        
        // Update the setlist in the setlists array immediately
        setSetlists(prevSetlists => 
          prevSetlists.map(s => 
            s.id === editingSetlist.id ? { ...s, ...updatedSetlist } : s
          )
        );
        
        // Also reload all setlists to ensure consistency
        loadSetlists();
        
        // Update selected setlist if it was the one being edited
        if (selectedSetlist && selectedSetlist.id === editingSetlist.id) {
          setSelectedSetlist(updatedSetlist);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update setlist');
      }
    } catch (error) {
      toast.error('Failed to update setlist');
      console.error('Error updating setlist:', error);
    }
  };

  const deleteSetlist = async (setlistId, setlistName) => {
    if (!window.confirm(`Are you sure you want to delete "${setlistName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/setlists/${setlistId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        toast.success('Setlist deleted successfully');
        if (selectedSetlist && selectedSetlist.id === setlistId) {
          setSelectedSetlist(null);
          setSongs([]);
        }
        loadSetlists();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete setlist');
      }
    } catch (error) {
      toast.error('Failed to delete setlist');
      console.error('Error deleting setlist:', error);
    }
  };

  const copySetlist = async (setlistId, setlistName) => {
    try {
      const response = await fetch(`/api/setlists/${setlistId}/copy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        const newSetlist = await response.json();
        toast.success(`Copied "${setlistName}" successfully`);
        
        // Reload setlists to show the new copy
        await loadSetlists();
        
        // Optionally select the new setlist
        setSelectedSetlist(newSetlist);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to copy setlist');
      }
    } catch (error) {
      toast.error('Failed to copy setlist');
      console.error('Error copying setlist:', error);
    }
  };


  const startEditSubSong = (subSong) => {
    setEditingSubSong(subSong);
    setEditSubSongForm({
      song_name: subSong.song_name,
      duration: subSong.duration || '',
      notes: subSong.notes || ''
    });
    setShowEditSubSongModal(true);
  };

  const saveEditSubSong = async (e) => {
    e.preventDefault();
    if (!editSubSongForm.song_name.trim()) {
      toast.error('Please enter a sub-song name');
      return;
    }

    try {
      const response = await fetch(`/api/sub-songs/${editingSubSong.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify(editSubSongForm)
      });

      if (response.ok) {
        toast.success('Sub-song updated successfully');
        setShowEditSubSongModal(false);
        setEditingSubSong(null);
        setEditSubSongForm({ song_name: '', duration: '', notes: '' });
        loadSongs(); // Reload songs to get updated sub-songs
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update sub-song');
      }
    } catch (error) {
      console.error('Error updating sub-song:', error);
      toast.error('Error updating sub-song');
    }
  };

  const deleteSubSong = async (subSongId, subSongName) => {
    if (!window.confirm(`Are you sure you want to delete sub-song "${subSongName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/sub-songs/${subSongId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        toast.success('Sub-song deleted successfully');
        loadSongs(); // Reload songs to get updated sub-songs
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete sub-song');
      }
    } catch (error) {
      console.error('Error deleting sub-song:', error);
      toast.error('Error deleting sub-song');
    }
  };

  const handleWatermarkUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type (PNG or SVG)
    if (!file.type.includes('png') && !file.type.includes('svg')) {
      toast.error('Please upload a PNG or SVG file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      // Convert to base64 (remove data:image/png;base64, prefix)
      const base64Data = e.target.result.split(',')[1];
      setNewSetlistWatermark(base64Data);
      toast.success('Watermark uploaded successfully');
    };
    reader.readAsDataURL(file);
  };

  const removeWatermark = () => {
    setNewSetlistWatermark(null);
    toast.success('Watermark removed');
  };

  const handleEditWatermarkUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type (PNG or SVG)
    if (!file.type.includes('png') && !file.type.includes('svg')) {
      toast.error('Please upload a PNG or SVG file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      // Convert to base64 (remove data:image/png;base64, prefix)
      const base64Data = e.target.result.split(',')[1];
      setEditWatermark(base64Data);
      toast.success('Watermark uploaded successfully');
    };
    reader.readAsDataURL(file);
  };

  const removeEditWatermark = () => {
    setEditWatermark(null);
    toast.success('Watermark removed');
  };

  const generateSetlistPDF = async (setlistId, setlistName, includeNotes = true, includeMedleys = true) => {
    try {
      const response = await fetch(`/api/setlists/${setlistId}/pdf?include_notes=${includeNotes}&include_medleys=${includeMedleys}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `setlist_${setlistName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        const notesText = includeNotes ? 'with notes' : 'without notes';
        const medleyText = includeMedleys ? 'with medleys' : 'without medleys';
        toast.success(`PDF generated successfully (${medleyText}, ${notesText})`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF');
    }
  };

  const generateSetlistPDFExcludeBlinded = async (setlistId, setlistName, includeNotes = true, includeMedleys = true) => {
    try {
      const response = await fetch(`/api/setlists/${setlistId}/pdf?include_notes=${includeNotes}&include_medleys=${includeMedleys}&exclude_blinded=true`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `setlist_${setlistName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('PDF generated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF');
    }
  };

  const generateTitlesOnlyPDF = async (setlistId, setlistName) => {
    try {
      const response = await fetch(`/api/setlists/${setlistId}/pdf/titles-only`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `setlist_${setlistName.replace(/[^a-zA-Z0-9]/g, '_')}_titles_only.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Titles-only PDF generated successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate titles-only PDF');
      }
    } catch (error) {
      console.error('Error generating titles-only PDF:', error);
      toast.error('Error generating titles-only PDF');
    }
  };

  const copySetlistToClipboard = async () => {
    if (!selectedSetlist || !songs.length) {
      toast.error('No setlist data to copy');
      return;
    }

    try {
      let output = `${selectedSetlist.name}\n`;
      if (selectedSetlist.description) {
        output += `${selectedSetlist.description}\n`;
      }
      output += `\n`;

      songs.forEach((song, index) => {
        const songNumber = index + 1;
        
        if (song.is_medley && song.sub_songs && song.sub_songs.length > 0) {
          // Medley with sub-songs
          output += `${songNumber}. ${song.song_name}:\n`;
          song.sub_songs.forEach((subSong, subIndex) => {
            const subOutput = `   ${String.fromCharCode(97 + subIndex)}. ${subSong.song_name}`;
            if (subSong.notes && subSong.notes.trim()) {
              output += `${subOutput} (${subSong.notes.trim()})\n`;
            } else {
              output += `${subOutput}\n`;
            }
          });
        } else {
          // Regular song
          const songOutput = `${songNumber}. ${song.song_name}`;
          if (song.notes && song.notes.trim()) {
            output += `${songOutput} (${song.notes.trim()})\n`;
          } else {
            output += `${songOutput}\n`;
          }
        }
      });

      await navigator.clipboard.writeText(output);
      toast.success('Setlist copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy setlist');
    }
  };

  const exportSetlistToCSV = () => {
    if (!selectedSetlist || !songs.length) {
      toast.error('No setlist data to export');
      return;
    }

    try {
      // CSV header
      let csvContent = 'song_name,duration,notes,parent_song\n';

      // Helper function to properly escape CSV fields
      const escapeCSV = (field) => {
        if (field === null || field === undefined || field === '') {
          return '""';
        }
        
        // Convert to string
        let str = String(field);
        
        // Escape quotes by doubling them
        str = str.replace(/"/g, '""');
        
        // Always wrap in quotes to handle commas, newlines, etc.
        return `"${str}"`;
      };

      songs.forEach((song) => {
        if (song.is_medley && song.sub_songs && song.sub_songs.length > 0) {
          // For medleys, export the main song first, then sub-songs
          const mainSongRow = [
            escapeCSV(song.song_name),
            escapeCSV(song.duration),
            escapeCSV(song.notes),
            '""' // empty parent_song for main medley
          ].join(',');
          csvContent += mainSongRow + '\n';

          // Then export each sub-song with the medley as parent
          song.sub_songs.forEach((subSong) => {
            const subSongRow = [
              escapeCSV(subSong.song_name),
              escapeCSV(subSong.duration),
              escapeCSV(subSong.notes),
              escapeCSV(song.song_name) // parent_song is the medley name
            ].join(',');
            csvContent += subSongRow + '\n';
          });
        } else {
          // Regular song
          const songRow = [
            escapeCSV(song.song_name),
            escapeCSV(song.duration),
            escapeCSV(song.notes),
            '""' // empty parent_song for regular songs
          ].join(',');
          csvContent += songRow + '\n';
        }
      });

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${selectedSetlist.name}_setlist.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const startEditSong = (song) => {
    setEditingSong(song);
    setEditSongForm({
      song_name: song.song_name,
      duration: song.duration || '',
      notes: song.notes || '',
      is_medley: song.is_medley || false,
      is_section: song.is_section || false
    });
    setShowEditSongModal(true);
  };

  const saveEditSong = async (e) => {
    e.preventDefault();
    if (!editSongForm.song_name.trim()) {
      toast.error('Please enter a song name');
      return;
    }

    try {
      const response = await fetch(`/api/songs/${editingSong.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({
          song_name: editSongForm.song_name.trim(),
          duration: editSongForm.is_section ? null : (editSongForm.duration.trim() || null),
          notes: editSongForm.notes.trim() || null,
          is_medley: editSongForm.is_section ? false : editSongForm.is_medley,
          is_section: editSongForm.is_section
        })
      });

      if (response.ok) {
        toast.success('Song updated successfully');
        setShowEditSongModal(false);
        setEditingSong(null);
        setEditSongForm({ song_name: '', duration: '', notes: '', is_medley: false, is_section: false });
        loadSongs();
        loadSetlists(); // Refresh setlist counts
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update song');
      }
    } catch (error) {
      toast.error('Failed to update song');
      console.error('Error updating song:', error);
    }
  };

  const handleDragStart = (e, song) => {
    setDraggedSong(song);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetSong) => {
    e.preventDefault();
    
    if (!draggedSong || draggedSong.id === targetSong.id) {
      setDraggedSong(null);
      return;
    }

    // Find the current positions
    const draggedIndex = songs.findIndex(s => s.id === draggedSong.id);
    const targetIndex = songs.findIndex(s => s.id === targetSong.id);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Create new order
    const newSongs = [...songs];
    const [movedSong] = newSongs.splice(draggedIndex, 1);
    newSongs.splice(targetIndex, 0, movedSong);

    // Update order indices
    const updatedOrders = newSongs.map((song, index) => ({
      id: song.id,
      order_index: index + 1
    }));

    // Optimistically update UI
    setSongs(newSongs.map((song, index) => ({
      ...song,
      order_index: index + 1
    })));

    try {
      const response = await fetch(`/api/setlists/${selectedSetlist.id}/songs/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify(updatedOrders)
      });

      if (response.ok) {
        toast.success('Songs reordered successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reorder songs');
        // Revert on error
        loadSongs();
      }
    } catch (error) {
      toast.error('Failed to reorder songs');
      console.error('Error reordering songs:', error);
      // Revert on error
      loadSongs();
    }

    setDraggedSong(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
    } else {
      toast.error('Please select a CSV file');
      e.target.value = '';
    }
  };

  const importFromCSV = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await fetch(`/api/setlists/${selectedSetlist.id}/import-csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.message}`);
        setShowImportModal(false);
        setSelectedFile(null);
        loadSongs();
        loadSetlists(); // Refresh setlist counts
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to import CSV');
      }
    } catch (error) {
      toast.error('Failed to import CSV');
      console.error('Error importing CSV:', error);
    }
  };

  // Medley management functions
  const toggleMedleyExpansion = (songId) => {
    const newExpanded = new Set(expandedMedleys);
    if (newExpanded.has(songId)) {
      newExpanded.delete(songId);
    } else {
      newExpanded.add(songId);
    }
    setExpandedMedleys(newExpanded);
  };

  const openAddSubSongModal = (medley) => {
    setSelectedMedley(medley);
    setShowAddSubSongModal(true);
  };

  const addSubSong = async (e) => {
    e.preventDefault();
    if (!newSubSong.song_name.trim()) {
      toast.error('Please enter a sub-song name');
      return;
    }

    try {
      const response = await fetch(`/api/songs/${selectedMedley.id}/sub-songs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({
          song_name: newSubSong.song_name.trim(),
          duration: newSubSong.duration.trim() || null,
          notes: newSubSong.notes.trim() || null
        })
      });

      if (response.ok) {
        toast.success('Sub-song added successfully');
        setNewSubSong({ song_name: '', duration: '', notes: '' });
        setShowAddSubSongModal(false);
        loadSongs(); // Refresh to show sub-songs
      } else {
        try {
          const error = await response.json();
          // Handle validation errors properly
          if (error.detail) {
            if (Array.isArray(error.detail)) {
              // Pydantic validation errors
              const errorMsg = error.detail.map(err => err.msg).join(', ');
              toast.error(`Validation error: ${errorMsg}`);
            } else {
              toast.error(error.detail);
            }
          } else {
            toast.error('Failed to add sub-song');
          }
        } catch (parseError) {
          toast.error('Failed to add sub-song');
        }
      }
    } catch (error) {
      toast.error('Failed to add sub-song');
      console.error('Error adding sub-song:', error);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <p>Loading setlists...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '15px', 
          marginBottom: '20px',
          flexWrap: 'wrap',
          overflow: 'hidden'
        }}>
          <button className="btn btn-secondary" onClick={onBack}>
            <ArrowLeft size={16} />
            Back to Events
          </button>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            Setlists (New Design)
          </h2>
          <div style={{ 
            marginLeft: 'auto', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            flexShrink: 0
          }}>
            <button 
              className="btn"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={16} />
              New Setlist
            </button>
          </div>
        </div>

        <div style={{ 
          display: 'flex',
          flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
          gap: '20px',
          overflow: 'hidden'
        }}>
          {/* Setlists List */}
          <div style={{ 
            flex: selectedSetlist && window.innerWidth > 768 ? '1' : 'none',
            minWidth: '300px',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            <h3>Your Setlists</h3>
            {setlists.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--muted)', padding: '40px' }}>
                No setlists created yet. Create your first setlist to get started!
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {setlists.map((setlist) => (
                  <div
                    key={setlist.id}
                    style={{
                      padding: '15px',
                      border: selectedSetlist?.id === setlist.id ? '2px solid var(--accent)' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: selectedSetlist?.id === setlist.id ? 'var(--setlist-selected)' : 'var(--setlist-surface)',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => setSelectedSetlist(setlist)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
                          {setlist.name}
                          {setlist.total_duration && (
                            <span style={{ 
                              marginLeft: '8px',
                              backgroundColor: 'var(--setlist-success)', 
                              color: '#fff', 
                              padding: '2px 6px', 
                              borderRadius: '4px', 
                              fontSize: '11px', 
                              fontWeight: 'bold' 
                            }}>
                              {setlist.total_duration}
                            </span>
                          )}
                        </div>
                        {setlist.description && (
                          <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '8px' }}>
                            {setlist.description}
                          </div>
                        )}
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          Created: {new Date(setlist.created_at).toLocaleDateString()}
                          <span> • {setlist.songCount || 0} songs</span>
                          {setlist.calculatedDuration && setlist.calculatedDuration !== '0:00' && (
                            <span> • {setlist.calculatedDuration} total</span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px', marginLeft: '10px' }}>
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditSetlist(setlist);
                          }}
                          title="Edit Setlist"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Edit size={12} />
                        </button>
                        <button
                          className="btn btn-info btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            copySetlist(setlist.id, setlist.name);
                          }}
                          title="Copy Setlist"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            generateTitlesOnlyPDF(setlist.id, setlist.name);
                          }}
                          title="Generate Titles-Only PDF (maximized visibility)"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <FileText size={12} />
                        </button>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            generateSetlistPDF(setlist.id, setlist.name, false, false); // Clean PDF (default)
                          }}
                          title="Generate Clean PDF (no notes, no medleys)"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Download size={12} />
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSetlist(setlist.id, setlist.name);
                          }}
                          title="Delete Setlist"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Setlist Songs */}
          {selectedSetlist && (
            <div style={{ 
              flex: window.innerWidth > 768 ? '2' : 'none',
              minWidth: '0',
              overflow: 'hidden'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px', 
                marginBottom: '15px',
                flexWrap: 'wrap',
                overflow: 'hidden'
              }}>
                <h3 style={{ 
                  margin: 0, 
                  flexShrink: 1,
                  minWidth: '0',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>{selectedSetlist.name}</h3>
                <button 
                  className="btn btn-success"
                  onClick={() => setShowAddSongModal(true)}
                >
                  <Plus size={16} />
                  Add Song
                </button>
                <button 
                  className="btn btn-info"
                  onClick={() => setShowImportModal(true)}
                >
                  <Upload size={16} />
                  Import CSV
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={copySetlistToClipboard}
                  title="Copy setlist to clipboard for sharing"
                >
                  <Copy size={16} />
                  Copy
                </button>
                <button 
                  className="btn btn-outline-secondary"
                  onClick={exportSetlistToCSV}
                  title="Export setlist as CSV file"
                >
                  <Download size={16} />
                  Export CSV
                </button>
                <div style={{ position: 'relative', display: 'inline-flex' }}>
                  <button 
                    className="btn btn-secondary"
                    onClick={generatePDFClean}
                    style={{ 
                      borderTopRightRadius: 0, 
                      borderBottomRightRadius: 0,
                      borderRight: 'none'
                    }}
                  >
                    <FileText size={16} style={{ marginRight: '8px' }} />
                    Generate PDF
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setShowPDFDropdown(!showPDFDropdown)}
                    style={{ 
                      borderTopLeftRadius: 0, 
                      borderBottomLeftRadius: 0,
                      paddingLeft: '8px', 
                      paddingRight: '8px',
                      borderLeft: '1px solid rgba(255,255,255,0.2)'
                    }}
                  >
                    ▼
                  </button>
                  {showPDFDropdown && (
                    <div 
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        zIndex: 1000,
                        backgroundColor: 'var(--setlist-surface)',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        minWidth: '200px',
                        marginTop: '2px'
                      }}
                    >
                      <button 
                        onClick={generatePDFWithMedleys}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '8px 12px',
                          border: 'none',
                          background: 'none',
                          textAlign: 'left',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--setlist-muted)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <strong>PDF with Medleys</strong><br />
                        <small style={{ color: 'var(--muted)' }}>Main songs + sub-songs</small>
                      </button>
                      <button 
                        onClick={generatePDFComplete}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '8px 12px',
                          border: 'none',
                          background: 'none',
                          textAlign: 'left',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--setlist-muted)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <strong>Complete PDF</strong><br />
                        <small style={{ color: 'var(--muted)' }}>Medleys + notes</small>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {songs.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--muted)', padding: '40px' }}>
                  No songs in this setlist yet. Add some songs to get started!
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(() => {
                    let songNumber = 0; // Track song numbers separately, skipping sections
                    return songs.map((song, index) => {
                    if (!song.is_section) songNumber++;
                    const currentSongNumber = songNumber;
                    return (
                    <React.Fragment key={song.id}>
                      {/* Section Divider - special styling, no number */}
                      {song.is_section ? (
                        <div
                          draggable
                          onDragStart={(e) => handleDragStart(e, song)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, song)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '15px',
                            padding: '10px 15px',
                            borderRadius: '0',
                            backgroundColor: 'transparent',
                            borderBottom: '2px solid var(--setlist-section-accent)',
                            marginTop: index > 0 ? '12px' : '0',
                            cursor: 'move',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{ cursor: 'grab', color: 'var(--setlist-section-accent)' }}>
                            <GripVertical size={16} />
                          </div>
                          <div style={{
                            flex: 1,
                            fontWeight: '700',
                            fontSize: '15px',
                            color: 'var(--setlist-section-text)',
                            textTransform: 'uppercase',
                            letterSpacing: '2px'
                          }}>
                            {song.song_name}
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => startEditSong(song)}
                              title="Edit Section"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              <Edit size={12} />
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => deleteSong(song.id, song.song_name)}
                              title="Delete Section"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ) : (
                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, song)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, song)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '15px',
                          padding: '15px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          backgroundColor: draggedSong?.id === song.id ? 'var(--setlist-drag)' : (song.is_blinded ? 'var(--setlist-warning)' : 'var(--setlist-muted)'),
                          cursor: 'move',
                          transition: 'all 0.2s'
                        }}
                      >
                      {/* Drag Handle */}
                      <div style={{ cursor: 'grab', color: 'var(--muted)' }}>
                        <GripVertical size={16} />
                      </div>

                      {/* Song Number - skips sections */}
                      <div style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--accent)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}>
                        {currentSongNumber}
                      </div>
                      
                      {/* Song Info */}
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          {song.is_medley && (
                            <button
                              onClick={() => toggleMedleyExpansion(song.id)}
                              style={{ 
                                background: 'none', 
                                border: 'none', 
                                cursor: 'pointer',
                                padding: '2px',
                                color: 'var(--muted)'
                              }}
                            >
                              {expandedMedleys.has(song.id) ? 
                                <ChevronDown size={16} /> : 
                                <ChevronRight size={16} />
                              }
                            </button>
                          )}
                          <div style={{ 
                            fontWeight: '600', 
                            fontSize: '16px',
                            textDecoration: song.is_blinded ? 'line-through' : 'none',
                            opacity: song.is_blinded ? 0.7 : 1
                          }}>
                            {song.song_name}
                            {song.is_medley && (
                              <span style={{ 
                                marginLeft: '8px',
                                backgroundColor: 'var(--setlist-section-accent)', 
                                color: '#fff', 
                                padding: '2px 6px', 
                                borderRadius: '4px', 
                                fontSize: '10px', 
                                fontWeight: 'bold' 
                              }}>
                                MEDLEY
                              </span>
                            )}
                          </div>
                          {song.duration && (
                            <span style={{ 
                              backgroundColor: 'var(--setlist-success)', 
                              color: '#fff', 
                              padding: '2px 6px', 
                              borderRadius: '4px', 
                              fontSize: '11px', 
                              fontWeight: 'bold' 
                            }}>
                              {song.duration}
                            </span>
                          )}
                        </div>
                        
                        {song.notes && (
                          <div style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>
                            {song.notes}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {song.is_medley && (
                          <button
                            className="btn btn-info btn-sm"
                            onClick={() => openAddSubSongModal(song)}
                            title="Add Sub-Song"
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            <Plus size={12} />
                          </button>
                        )}
                        <button
                          className={`btn btn-sm ${song.is_blinded ? 'btn-warning' : 'btn-secondary'}`}
                          onClick={() => toggleBlind(song)}
                          title={song.is_blinded ? 'Un-blind song' : 'Blind song'}
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          {song.is_blinded ? <Eye size={12} /> : <EyeOff size={12} />}
                        </button>
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={() => startEditSong(song)}
                          title="Edit Song"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Edit size={12} />
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => deleteSong(song.id, song.song_name)}
                          title="Delete Song"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    )}

                    {/* Sub-songs for expanded medleys - outside the ternary */}
                    {!song.is_section && song.is_medley && expandedMedleys.has(song.id) && song.sub_songs && (
                      <div style={{ marginLeft: '60px', marginTop: '8px' }}>
                        {song.sub_songs.map((subSong, subIndex) => (
                          <div
                            key={subSong.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '15px',
                              padding: '10px 15px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '4px',
                              backgroundColor: 'var(--setlist-muted)',
                              marginBottom: '4px'
                            }}
                          >
                            {/* Sub-song number */}
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              backgroundColor: 'var(--setlist-section-accent)',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '12px'
                            }}>
                              {currentSongNumber}.{subIndex + 1}
                            </div>

                            {/* Sub-song info */}
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ fontWeight: '500', fontSize: '14px' }}>
                                  {subSong.song_name}
                                </div>
                                {subSong.duration && (
                                  <span style={{
                                    backgroundColor: 'var(--setlist-success)',
                                    color: '#fff',
                                    padding: '1px 4px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}>
                                    {subSong.duration}
                                  </span>
                                )}
                              </div>
                              {subSong.notes && (
                                <div style={{ fontSize: '11px', color: 'var(--muted)', fontStyle: 'italic' }}>
                                  {subSong.notes}
                                </div>
                              )}
                            </div>

                            {/* Sub-song actions */}
                            <div style={{ display: 'flex', gap: '2px' }}>
                              <button
                                className="btn btn-warning btn-sm"
                                onClick={() => startEditSubSong(subSong)}
                                title="Edit Sub-Song"
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                              >
                                <Edit size={10} />
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => deleteSubSong(subSong.id, subSong.song_name)}
                                title="Delete Sub-Song"
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                  );
                  })
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Setlist Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Create New Setlist</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={createSetlist}>
              <div className="form-group">
                <label>Setlist Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={newSetlistName}
                  onChange={(e) => setNewSetlistName(e.target.value)}
                  placeholder="e.g. Summer Festival 2024"
                  required
                />
              </div>
              <div className="form-group">
                <label>Total Duration (Optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={newSetlistDuration}
                  onChange={(e) => setNewSetlistDuration(e.target.value)}
                  placeholder="e.g. 45:00, 1:30:00"
                />
              </div>
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  className="form-control"
                  value={newSetlistDescription}
                  onChange={(e) => setNewSetlistDescription(e.target.value)}
                  placeholder="Brief description of this setlist..."
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Watermark (Optional)</label>
                {!newSetlistWatermark ? (
                  <div>
                    {/* Recent watermarks */}
                    {recentWatermarks.length > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '5px', display: 'block' }}>
                          Recent Watermarks:
                        </label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {recentWatermarks.map((wm) => (
                            <button
                              key={wm.setlist_id}
                              type="button"
                              onClick={() => {
                                setNewSetlistWatermark(wm.watermark_data);
                                toast.success(`Using watermark from "${wm.setlist_name}"`);
                              }}
                              style={{
                                padding: '6px 10px',
                                fontSize: '12px',
                                backgroundColor: 'var(--setlist-section)',
                                border: '1px solid var(--setlist-section-accent)',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                              title={`Use watermark from ${wm.setlist_name}`}
                            >
                              <img
                                src={`data:image/png;base64,${wm.watermark_data}`}
                                alt=""
                                style={{ height: '16px', width: 'auto', opacity: 0.8 }}
                              />
                              <span>{wm.setlist_name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="file"
                        accept=".png,.svg"
                        onChange={handleWatermarkUpload}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    padding: '8px 12px', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    backgroundColor: 'var(--setlist-muted)'
                  }}>
                    <span style={{ flex: 1, color: '#28a745' }}>
                      Watermark file uploaded
                    </span>
                    <button 
                      type="button" 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        // Create a temporary image to show preview
                        const img = new Image();
                        img.src = `data:image/png;base64,${newSetlistWatermark}`;
                        const win = window.open('', '_blank');
                        win.document.write(`<img src="${img.src}" style="max-width:100%; max-height:100%;" />`);
                      }}
                      title="Preview watermark"
                    >
                      <Eye size={16} aria-hidden="true" />
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-danger btn-sm"
                      onClick={removeWatermark}
                      title="Remove watermark"
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                    <input
                      type="file"
                      accept=".png,.svg"
                      onChange={handleWatermarkUpload}
                      style={{ display: 'none' }}
                      id="new-watermark-replace"
                    />
                    <label 
                      htmlFor="new-watermark-replace"
                      className="btn btn-outline-secondary btn-sm"
                      style={{ margin: 0, cursor: 'pointer' }}
                      title="Replace watermark"
                    >
                      Replace
                    </label>
                  </div>
                )}
                <small style={{ color: 'var(--muted)', fontSize: '12px' }}>
                  Upload a PNG or SVG file to use as a watermark (max 5MB). Will appear as a faint background in PDFs.
                </small>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn">
                  Create Setlist
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Song Modal */}
      {showAddSongModal && selectedSetlist && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{newSong.is_section ? 'Add Section Divider' : 'Add Song'} to {selectedSetlist.name}</h2>
              <button className="close-btn" onClick={() => setShowAddSongModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={addSong}>
              {/* Section toggle at the top */}
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px',
                  backgroundColor: newSong.is_section ? 'var(--setlist-section)' : 'var(--setlist-muted)',
                  borderRadius: '6px',
                  border: newSong.is_section ? '2px solid var(--setlist-section-accent)' : '1px solid #e5e7eb',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={newSong.is_section}
                    onChange={(e) => setNewSong({...newSong, is_section: e.target.checked, is_medley: false})}
                  />
                  <span style={{ fontWeight: newSong.is_section ? '600' : '400', color: newSong.is_section ? 'var(--setlist-section-text)' : 'inherit' }}>
                    This is a section divider (e.g., "Act 1", "Interval", "Encore")
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>{newSong.is_section ? 'Section Title' : 'Song Name'}</label>
                <input
                  type="text"
                  className="form-control"
                  value={newSong.song_name}
                  onChange={(e) => setNewSong({...newSong, song_name: e.target.value})}
                  placeholder={newSong.is_section ? 'e.g. Act 1, Interval, Encore' : 'e.g. Jump, Holding Out for a Hero'}
                  required
                />
              </div>

              {!newSong.is_section && (
                <>
                  <div className="form-group">
                    <label>Duration (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newSong.duration}
                      onChange={(e) => setNewSong({...newSong, duration: e.target.value})}
                      placeholder="e.g. 3:45, 2:30"
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes (Optional)</label>
                    <textarea
                      className="form-control"
                      value={newSong.notes}
                      onChange={(e) => setNewSong({...newSong, notes: e.target.value})}
                      placeholder="Any special notes about this song..."
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={newSong.is_medley}
                        onChange={(e) => setNewSong({...newSong, is_medley: e.target.checked})}
                      />
                      This is a medley (contains multiple sub-songs)
                    </label>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddSongModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn" style={newSong.is_section ? { backgroundColor: 'var(--setlist-section-accent)' } : {}}>
                  {newSong.is_section ? 'Add Section' : 'Add Song'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Song Modal */}
      {showEditSongModal && editingSong && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editSongForm.is_section ? 'Edit Section Divider' : 'Edit Song'}</h2>
              <button className="close-btn" onClick={() => setShowEditSongModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={saveEditSong}>
              {/* Section toggle */}
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px',
                  backgroundColor: editSongForm.is_section ? 'var(--setlist-section)' : 'var(--setlist-muted)',
                  borderRadius: '6px',
                  border: editSongForm.is_section ? '2px solid var(--setlist-section-accent)' : '1px solid #e5e7eb',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={editSongForm.is_section}
                    onChange={(e) => setEditSongForm({...editSongForm, is_section: e.target.checked, is_medley: false})}
                  />
                  <span style={{ fontWeight: editSongForm.is_section ? '600' : '400', color: editSongForm.is_section ? 'var(--setlist-section-text)' : 'inherit' }}>
                    This is a section divider (e.g., "Act 1", "Interval", "Encore")
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label>{editSongForm.is_section ? 'Section Title' : 'Song Name'}</label>
                <input
                  type="text"
                  className="form-control"
                  value={editSongForm.song_name}
                  onChange={(e) => setEditSongForm({...editSongForm, song_name: e.target.value})}
                  placeholder={editSongForm.is_section ? 'e.g. Act 1, Interval, Encore' : 'e.g. Jump, Holding Out for a Hero'}
                  required
                />
              </div>

              {!editSongForm.is_section && (
                <>
                  <div className="form-group">
                    <label>Duration (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editSongForm.duration}
                      onChange={(e) => setEditSongForm({...editSongForm, duration: e.target.value})}
                      placeholder="e.g. 3:45, 2:30"
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes (Optional)</label>
                    <textarea
                      className="form-control"
                      value={editSongForm.notes}
                      onChange={(e) => setEditSongForm({...editSongForm, notes: e.target.value})}
                      placeholder="Any special notes about this song..."
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={editSongForm.is_medley}
                        onChange={(e) => setEditSongForm({...editSongForm, is_medley: e.target.checked})}
                      />
                      This is a medley (contains multiple sub-songs)
                    </label>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditSongModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn" style={editSongForm.is_section ? { backgroundColor: 'var(--setlist-section-accent)' } : {}}>
                  {editSongForm.is_section ? 'Update Section' : 'Update Song'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Sub-Song Modal */}
      {showEditSubSongModal && editingSubSong && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Edit Sub-Song</h2>
              <button className="close-btn" onClick={() => setShowEditSubSongModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={saveEditSubSong}>
              <div className="form-group">
                <label>Sub-Song Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={editSubSongForm.song_name}
                  onChange={(e) => setEditSubSongForm({...editSubSongForm, song_name: e.target.value})}
                  placeholder="e.g. Intro, Verse 1, Chorus"
                  required
                />
              </div>
              <div className="form-group">
                <label>Duration (Optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={editSubSongForm.duration}
                  onChange={(e) => setEditSubSongForm({...editSubSongForm, duration: e.target.value})}
                  placeholder="e.g. 3:45, 2:30"
                />
              </div>
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  className="form-control"
                  value={editSubSongForm.notes}
                  onChange={(e) => setEditSubSongForm({...editSubSongForm, notes: e.target.value})}
                  placeholder="Any special notes about this sub-song..."
                  rows="3"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditSubSongModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && selectedSetlist && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Import Songs from CSV</h2>
              <button className="close-btn" onClick={() => setShowImportModal(false)}>
                ×
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <h4>CSV Format Instructions:</h4>
                <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '10px' }}>
                  Your CSV file should have these columns (header row required):
                </p>
                <div style={{ 
                  backgroundColor: 'var(--setlist-muted)', 
                  padding: '10px', 
                  borderRadius: '4px', 
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  marginBottom: '15px'
                }}>
                  <strong>song_name,duration,notes,parent_song</strong><br/>
                  Film Medley,10:00,,<br/>
                  Top Gun Theme,2:30,,Film Medley<br/>
                  Mission Impossible,3:00,,Film Medley<br/>
                  James Bond Theme,4:30,,Film Medley<br/>
                  Jump,3:45,High energy opener,<br/>
                  Queen Medley,12:38,,<br/>
                  Bohemian Rhapsody,6:00,,Queen Medley<br/>
                  We Will Rock You,3:38,,Queen Medley
                </div>
                <p style={{ fontSize: '12px', color: 'var(--muted)' }}>
                  • <strong>song_name</strong> is required (can also use "name" or "title")<br/>
                  • <strong>duration</strong>, <strong>notes</strong>, and <strong>parent_song</strong> are optional<br/>
                  • <strong>parent_song</strong>: Leave empty for main songs, specify parent name for sub-songs<br/>
                  • Medleys are automatically created when sub-songs reference them<br/>
                  • Songs will be added to the end of your current setlist
                </p>
              </div>

              <div className="form-group">
                <label>Select CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="form-control"
                />
                {selectedFile && (
                  <p style={{ fontSize: '12px', color: 'var(--setlist-success)', marginTop: '5px' }}>
                    Selected: {selectedFile.name}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowImportModal(false);
                    setSelectedFile(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn"
                  onClick={importFromCSV}
                  disabled={!selectedFile}
                >
                  <Upload size={16} style={{ marginRight: '6px' }} />
                  Import Songs
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Setlist Modal */}
      {showEditSetlistModal && editingSetlist && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Edit Setlist</h2>
              <button className="close-btn" onClick={cancelEditSetlist}>
                ×
              </button>
            </div>
            <form onSubmit={saveEditSetlist}>
              <div className="form-group">
                <label>Setlist Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  placeholder="e.g. Summer Festival 2024"
                  required
                />
              </div>
              <div className="form-group">
                <label>Total Duration (Optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={editForm.total_duration}
                  onChange={(e) => setEditForm({...editForm, total_duration: e.target.value})}
                  placeholder="e.g. 45:00, 1:30:00"
                />
              </div>
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  className="form-control"
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  placeholder="Brief description of this setlist..."
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Watermark (Optional)</label>
                {(editWatermark === undefined && !editingSetlist.watermark_data) || editWatermark === null ? (
                  <div>
                    {/* Recent watermarks for edit modal */}
                    {recentWatermarks.length > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '5px', display: 'block' }}>
                          Recent Watermarks:
                        </label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {recentWatermarks.filter(wm => wm.setlist_id !== editingSetlist?.id).map((wm) => (
                            <button
                              key={wm.setlist_id}
                              type="button"
                              onClick={() => {
                                setEditWatermark(wm.watermark_data);
                                toast.success(`Using watermark from "${wm.setlist_name}"`);
                              }}
                              style={{
                                padding: '6px 10px',
                                fontSize: '12px',
                                backgroundColor: 'var(--setlist-section)',
                                border: '1px solid var(--setlist-section-accent)',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                              title={`Use watermark from ${wm.setlist_name}`}
                            >
                              <img
                                src={`data:image/png;base64,${wm.watermark_data}`}
                                alt=""
                                style={{ height: '16px', width: 'auto', opacity: 0.8 }}
                              />
                              <span>{wm.setlist_name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="file"
                        accept=".png,.svg"
                        onChange={handleEditWatermarkUpload}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    padding: '8px 12px', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    backgroundColor: 'var(--setlist-muted)'
                  }}>
                    <span style={{ flex: 1, color: '#28a745' }}>
                      Watermark file uploaded
                    </span>
                    <button 
                      type="button" 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        // Create a temporary image to show preview
                        const img = new Image();
                        const watermarkData = editWatermark || editingSetlist.watermark_data;
                        img.src = `data:image/png;base64,${watermarkData}`;
                        const win = window.open('', '_blank');
                        win.document.write(`<img src="${img.src}" style="max-width:100%; max-height:100%;" />`);
                      }}
                      title="Preview watermark"
                    >
                      <Eye size={16} aria-hidden="true" />
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-danger btn-sm"
                      onClick={removeEditWatermark}
                      title="Remove watermark"
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                    <input
                      type="file"
                      accept=".png,.svg"
                      onChange={handleEditWatermarkUpload}
                      style={{ display: 'none' }}
                      id="edit-watermark-replace"
                    />
                    <label 
                      htmlFor="edit-watermark-replace"
                      className="btn btn-outline-secondary btn-sm"
                      style={{ margin: 0, cursor: 'pointer' }}
                      title="Replace watermark"
                    >
                      Replace
                    </label>
                  </div>
                )}
                <small style={{ color: 'var(--muted)', fontSize: '12px' }}>
                  {editWatermark || editingSetlist.watermark_data
                    ? 'Upload a new PNG or SVG file to replace the current watermark (max 5MB).'
                    : 'Upload a PNG or SVG file for watermark (max 5MB). Will appear as a faint background in PDFs.'
                  }
                </small>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={cancelEditSetlist}>
                  Cancel
                </button>
                <button type="submit" className="btn">
                  Update Setlist
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Sub-Song Modal */}
      {showAddSubSongModal && selectedMedley && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Add Sub-Song to {selectedMedley.song_name}</h2>
              <button className="close-btn" onClick={() => setShowAddSubSongModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={addSubSong}>
              <div className="form-group">
                <label>Sub-Song Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={newSubSong.song_name}
                  onChange={(e) => setNewSubSong({...newSubSong, song_name: e.target.value})}
                  placeholder="e.g. Top Gun Theme, Mission Impossible"
                  required
                />
              </div>
              <div className="form-group">
                <label>Duration (Optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={newSubSong.duration}
                  onChange={(e) => setNewSubSong({...newSubSong, duration: e.target.value})}
                  placeholder="e.g. 2:30, 3:00"
                />
              </div>
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  className="form-control"
                  value={newSubSong.notes}
                  onChange={(e) => setNewSubSong({...newSubSong, notes: e.target.value})}
                  placeholder="Any special notes about this sub-song..."
                  rows="2"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddSubSongModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn">
                  Add Sub-Song
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewSetlistView;
